<script lang="ts">
    import type {DateValueEditorLocalizations, DateValueEditorOptions, DateValueEditorValidations} from './types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControlOptions} from '../../forms/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {formControls} from '../../forms/use.form-controls';
    import {touched} from '../../shared/use.touched';
    import {style} from 'svelte-forms';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {getContext} from 'svelte';
    import {FormControlBuilder} from '../../forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import {onDestroy} from 'svelte';
    import {onlyDateFormatter, onlyDateParser, validatorFactory} from './utils';
    import {dateFormatter, dateParser} from '../_shared/date-time-utils';
    import {DateTime, Info} from 'luxon';
    import {required} from '../../forms/use.required';
    import {getCurrentLanguage, getLogger} from 'core/svelte-context/context';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {offset} from 'svelte-floating-ui/dom';
    import {autoPlacement} from '@floating-ui/core';
    import SveltyPicker from 'svelty-picker';
    import {SHOW_LOC_MESSAGES_LANGUAGE} from 'shared/providers/current-language.provider';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpPopoverPanel from 'shared/ui-widgets/popover/KpPopoverPanel.svelte';
    import {uuid} from 'shared/utils/custom-utils';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: DateValueEditorOptions;
    export let validations: DateValueEditorValidations;

    const currentLanguage = getCurrentLanguage();
    const localize: EditorsLocalizationFunction<DateValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([(value: string) => value.trim(), dateParser({customViewFormat: options.viewFormat})])
        .withModifiers([onlyDateParser(options)])
        .withFormatters([dateFormatter({customViewFormat: options.viewFormat}), onlyDateFormatter(options)])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    onDestroy(() => {
        subscription.unsubscribe();
    });

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const datetimePickerLocalization = (!!currentLanguage && currentLanguage !== SHOW_LOC_MESSAGES_LANGUAGE) ? createLocalizationForLocale(currentLanguage) : undefined;

    const popoverId = uuid()
    const [floatingRef, floatingContent] = createFloatingActions({
        placement: 'bottom-end',
        strategy: 'fixed',
        middleware: [
            offset(10),
            autoPlacement({
                allowedPlacements: ['top-end', 'bottom-end'],
            })
        ]
    });

    let inputElement: HTMLElement;

    function onPickerChange(event: CustomEvent<string>, pickerCloseHandler: () => void) {
        formControl.setViewValue(event.detail ?? '');
        pickerCloseHandler();
        inputElement.focus();
    }

    function createLocalizationForLocale(locale: string) {
        const copyLastElementToFront = (array: string[]) => {
            array.unshift(array.at(-1));
            return array;
        }

        return {
            cancelBtn: undefined,
            clearBtn: undefined,
            okBtn: undefined,
            todayBtn: undefined,
            timeView: localize('timeView'),
            backToDate: localize('backToDate'),
            days: copyLastElementToFront(Info.weekdays('long', {locale})), // weekday localization start at 0 with Sunday and ends at 7 also with sunday
            daysShort: copyLastElementToFront(Info.weekdays('short', {locale})), // weekday localization start at 0 with Sunday and ends at 7 also with sunday
            daysMin: copyLastElementToFront(Info.weekdays('short', {locale})), // weekday localization start at 0 with Sunday and ends at 7 also with sunday
            months: Info.months('long', {locale}),
            monthsShort: Info.months('short', {locale}),
            meridiem: Info.meridiems({locale}),
            suffix: ['st', 'nd', 'rd', 'th']
        };
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

<div class="input-group input-group-{size}">
    <input class="form-control input-{size}"
           type="text"
           id={editorId}
           name={editorName}
           {placeholder}
           disabled={isDisabled}
           use:required={validations.required}
           use:focusOnMount={isFocused}
           use:formControls={formControl}
           use:touched={{formControl}}
           use:style={{field: formControl.getFieldStateAsStore()}}
           use:ariaInvalid={formControl}
           use:ariaErrormessage={formControl}
           use:ariaDescribedby={inputDescription}

           bind:this={inputElement}
           data-main-input/>
    <span class="input-group-btn">
        <KpButton use={[floatingRef]} {isDisabled} popovertarget="{popoverId}">
            <IconedContent icon="calendar">
                <span class="sr-only">{localize('openCalendar')}</span>
            </IconedContent>
        </KpButton>
    </span>
</div>

<KpPopoverPanel id="{popoverId}"
                use={[floatingContent]}
                additionalClasses="datetime-picker-popover"
                on:popover-close={() => formControl.setTouched()}
                let:togglePopover>
    <div class="datetime-picker-container">
        <SveltyPicker inputClasses="hidden"
                      inputId="{editorId}-picker"
                      name="{editorName}-picker"
                      format={options.maximumGranularity === 'date-only' ? 'd.m.yyyy' : 'd.m.yyyy h:ii'}
                      initialDate={model ? DateTime.fromISO(model).toJSDate() : null}
                      startDate={validations.minDate ? DateTime.fromISO(validations.minDate).toJSDate() : null}
                      endDate={validations.maxDate ? DateTime.fromISO(validations.maxDate).toJSDate() : null}
                      i18n={datetimePickerLocalization}
                      disabled={isDisabled}
                      pickerOnly={true}
                      todayBtn={false}
                      clearBtn={false}
                      on:change={(event) => {onPickerChange(event, togglePopover)}}>
        </SveltyPicker>
    </div>
</KpPopoverPanel>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>

<Description {inputDescription}/>

<style lang="less">
    :global {
        .datetime-picker-popover {
            --popover-content-padding: 0;
        }
    }
    .datetime-picker-container {
        --sdt-wrap-shadow: none;
        :global(*) {
            font-size: 1.4rem;
        }
    }
</style>